import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  ThemeProvider,
  Typography,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { hasIdentifierTabs, identifierModesFor } from "../rewamp/context/FarmerContext";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import "../styles/Schemes.css";
import { ReactComponent as EditPencilIcon } from "../assets/rewamp/onboarding/edit-pencil-icon.svg";
import RewampFieldLabel from "../rewamp/components/schemeApply/formEngine/widgets/RewampFieldLabel";
import { useDispatch } from "react-redux";
import OTPinput from "./OTPinput";
import { SchemeService } from "../services/Schemes";
import { dispatchNotification } from "./Utils";
import AccessibleCircularProgress from "./AccessibleCircularProgress";
import generateRewampTheme from "../rewamp/theme";
import { AppLayoutContext } from "../contexts/AppLayoutContext";
import RewampFeedbackBanner from "../rewamp/components/RewampFeedbackBanner";
import RewampDbtHelperLinks from "../rewamp/components/RewampDbtHelperLinks";
import { RewampFormField } from "../rewamp/components/RewampFormField";
import {
  DBT_REGISTRATION_PORTAL_URL,
  LOGIN_CARD_MAX_WIDTH,
  OTP_RESEND_SECONDS,
} from "../rewamp/components/RewampOnboardingLayout";
import {
  isOtpErrorVariant,
  resolveOtpFeedbackVariant,
} from "../rewamp/utils/otpFeedbackVariant";
import { OTP_RATE_LIMIT_CODE } from "../rewamp/utils/otpApiErrors";
import useUrlDialog from "../Hooks/useUrlDialog";
import UrlDialog from "./dialog/UrlDialog";
import useDbtRegistrationHint from "../rewamp/hooks/useDbtRegistrationHint";
import { DbtRegistrationBadge } from "../rewamp/components/DbtRegistrationBadge";
import { useRewampLocalization, useRewampT, resolveFarmerVerifyHeader } from "../rewamp/localisation";

// OtpInputRendererBody always renders under the rewamp theme (see the OtpInputRenderer wrapper below).
// Standard visually-hidden pattern — keeps the "Verify using" fieldset legend
// accessible to screen readers even though Figma 639-82351 doesn't show it
// visually (the tab-styled radio pair is self-explanatory sighted).
const visuallyHiddenSx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// Figma 639-88408 — bordered card shell for the OTP step, shared by every
// OtpInputRenderer consumer (GRM's TrackService.js/GrmCreateForm.js included).
const otpCardSx = {
  p: (theme) => theme.palette.layout.cardPadding,
  backgroundColor: "background.paper",
  borderRadius: 4,
  border: 1,
  borderColor: "divider",
  boxShadow: (theme) => theme.palette.layout.cardShadow,
};

// "Didn't Receive? Retry in 00:00s" — design wants mm:ss, not a raw second count.
const formatCountdown = (seconds) => {
  const total = Math.max(0, seconds);
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

// Splits an already-interpolated "...{{time}}..." sentence around the known
// countdown substring so only the parameterised time value can be colored —
// language-agnostic (no dependency on where {{time}} sits in the sentence),
// unlike wrapping it in Trans-style markup in every locale's translation string.
const renderCountdownSentence = (sentence, countdown, timeSx) => {
  const idx = sentence.indexOf(countdown);
  if (idx === -1) return sentence;
  // A unit glued to the value ("00:30s") belongs to it; a space before the next
  // word (Hindi "{{time}} में") means the sentence continues, so nothing extends.
  const unit = sentence.slice(idx + countdown.length).match(/^\p{L}+/u)?.[0] ?? "";
  const end = idx + countdown.length + unit.length;
  return (
    <>
      {sentence.slice(0, idx)}
      <Box component="span" sx={timeSx}>
        {countdown}
        {unit}
      </Box>
      {sentence.slice(end)}
    </>
  );
};

const IDENTIFIER_MODE_LABEL_KEYS = { dbtId: "schemes.verifyByDbtId", mobile: "schemes.verifyByMobile" };

// Backend error codes with revamp copy; anything else still resolves through the legacy t() bundle.
const REWAMP_IDENTIFIER_ERRORS = {
  DBT_USER: ["FARMER_VERIFY.ERROR_DBT_USER", "This mobile number belongs to a DBT registered farmer."],
};

// Figma 639-70196 / 91-28910 — shared onboarding OTP vertical rhythm (8px grid).
const ONBOARDING_OTP_GAP_TITLE = 2;
const ONBOARDING_OTP_GAP_LABEL = 1.5;
const ONBOARDING_OTP_GAP_INPUTS = 2;

const formatOtpMobileDisplay = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  if (digits.length === 10) return digits;
  return String(value ?? "");
};

// A code with no translation comes back unchanged; prose never lacks whitespace, codes do.
const otpFeedbackText = (t, code) => {
  const text = t(code);
  return text === code && !/\s/.test(code) ? t("SOMETHING_WENT_WRONG") : text;
};

const renderOnboardingEditPencil = (t, onEditNumber) => (
  <IconButton
    size="small"
    aria-label={t("schemes.editMobileNumber")}
    onClick={onEditNumber}
    sx={{
      color: "primary.main",
      p: 0.5,
      minWidth: 24,
      minHeight: 24,
    }}
  >
    <EditPencilIcon width={18} height={18} aria-hidden focusable="false" />
  </IconButton>
);

const OtpInputRendererBody = (props) => {
  const {
    mobileNumber,
    otpValue,
    setOtpValue,
    otpValidationError,
    setOtpValidationError,
    t,
    handleVerifyOtp,
    isSubmit = false,
    onResendOtp,
    onEditNumber,
    bare = false,
    waitTime,
    setWaitTime,
    verificationLoading = false,
    onboardingChrome = false,
    otpSubheaderKey = "",
    hideAccountVerificationTitle = false,
    mobileNumberDisplay = "",
    layout = "stacked",
  } = props;

  const theme = useTheme();
  const otpMobileDisplay = formatOtpMobileDisplay(mobileNumberDisplay || mobileNumber);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    } else if (timeLeft === 0) {
      setWaitTime(null);
      // Countdown ran out — surface the expiry state on every OtpInputRenderer
      // consumer (not just onboardingChrome), not only when Verify is clicked.
      setOtpValidationError({ message: "ONBOARDING_OTP_EXPIRED" });
    }
  }, [timeLeft, setWaitTime, setOtpValidationError]);

  useEffect(() => {
    setTimeLeft(waitTime);
  }, [waitTime]);

  const handleOTPClick = async () => {
    if (otpValue.length < 6) {
      setOtpValidationError({ message: "COMMON_OTP_REQUIRED" });
      return;
    }
    if (onboardingChrome && timeLeft === 0 && (waitTime === null || waitTime === 0)) {
      setOtpValidationError({ message: "ONBOARDING_OTP_EXPIRED" });
      return;
    }
    handleVerifyOtp();
  };

  // "inline" sits in a full-width page column, so the digit row must stay at
  // content width and left-align — centring it strands the digits mid-page,
  // away from both the label above and the Verify button beside it.
  const isInline = layout === "inline";
  const otpHasError = isOtpErrorVariant(otpValidationError);
  const otpFeedbackVariant = resolveOtpFeedbackVariant(otpValidationError?.message);

  const otpBoxSx = {
    width: theme.spacing(6),
    height: theme.spacing(6.5),
    borderRadius: onboardingChrome ? 2.5 : 2,
    border: `1px solid ${otpHasError ? theme.palette.formMessage.error : theme.palette.primary.interactive}`,
    backgroundColor: otpHasError
      ? theme.palette.feedbackBanner.errorBg
      : theme.palette.primary.softSurface || theme.palette.background.default,
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: "1.25rem",
    textAlign: "center",
    "&:focus-visible": {
      outline: `3px solid ${theme.palette.accent?.butterscotch}`,
      outlineOffset: "2px",
    },
  };

  const renderOtpFeedback = () => {
    if (!otpValidationError?.message || !otpFeedbackVariant) return null;
    return (
      <RewampFeedbackBanner
        variant={otpFeedbackVariant}
        message={otpFeedbackText(t, otpValidationError.message)}
        sx={{ maxWidth: onboardingChrome || isInline ? "100%" : "386px" }}
      />
    );
  };

  const renderResendRow = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: isInline ? "flex-start" : "flex-end",
        width: isInline ? "auto" : "100%",
        pb: onboardingChrome ? 2 : 0,
        pt: onboardingChrome ? 0 : 0,
      }}
    >
      {timeLeft > 0 ? (
        onboardingChrome ? (
          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: "neutral.disabled", textAlign: "right" }}>
            {renderCountdownSentence(
              t("ONBOARDING_OTP_RETRY_IN", { time: formatCountdown(timeLeft) }),
              formatCountdown(timeLeft),
              { color: "primary.main", fontWeight: 600 }
            )}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            {renderCountdownSentence(
              t("schemes.resendOtpIn", { time: formatCountdown(timeLeft) }),
              formatCountdown(timeLeft),
              { color: "primary.main", fontWeight: 600, textDecoration: "underline" }
            )}
          </Typography>
        )
      ) : (
        <Button
          variant="text"
          size="small"
          onClick={() => {
            // Resending clears whatever error (e.g. expired-OTP) was showing
            // for the old code — it no longer applies to the new one.
            setOtpValidationError({ message: "" });
            onResendOtp?.();
          }}
          sx={{
            textDecoration: "underline",
            color: theme.palette.primary.main,
            fontWeight: onboardingChrome ? 600 : 400,
            fontSize: onboardingChrome ? 12 : undefined,
            lineHeight: onboardingChrome ? "18px" : undefined,
            px: 0,
          }}
        >
          {t("COMMON_RESEND_OTP")}
        </Button>
      )}
    </Box>
  );

  const renderOnboardingChrome = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      {!hideAccountVerificationTitle ? (
        <Typography
          variant="h2"
          component="p"
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "30px",
            textAlign: "center",
            color: "text.primary",
            width: "100%",
          }}
        >
          {t("ONBOARDING_ACCOUNT_VERIFICATION_TITLE")}
        </Typography>
      ) : null}

      <Box
        sx={{
          mt: hideAccountVerificationTitle ? 0 : ONBOARDING_OTP_GAP_TITLE,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {otpSubheaderKey ? (
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              lineHeight: "24px",
              textAlign: "center",
              color: "text.primary",
              width: "100%",
            }}
          >
            {t(otpSubheaderKey)}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              lineHeight: "1.125rem",
              color: "neutral.body",
              textAlign: "center",
            }}
          >
            {t("ONBOARDING_OTP_SENT_PREFIX")}
            <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
              91-{otpMobileDisplay}
            </Box>
          </Typography>
          {onEditNumber ? renderOnboardingEditPencil(t, onEditNumber) : null}
        </Box>
      </Box>

      <Box sx={{ mt: ONBOARDING_OTP_GAP_LABEL, width: "100%", display: "flex", justifyContent: "center" }}>
        <RewampFieldLabel
          label={t("ONBOARDING_OTP_ENTER_LABEL")}
          required
          component="p"
          sx={{ textAlign: "center" }}
        />
      </Box>
    </Box>
  );

  const renderOtpInputBlock = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: isInline ? "auto" : "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: isInline ? "flex-start" : "center",
          flexWrap: "wrap",
        }}
      >
        <OTPinput
          boxGap={1.5}
          otp={otpValue}
          setOtp={setOtpValue}
          setSubmissionStatus={setOtpValidationError}
          inputSx={otpBoxSx}
        />
      </Box>
      {renderOtpFeedback()}
      {renderResendRow()}
    </Box>
  );

  return (
    <Box sx={bare ? undefined : otpCardSx}>
      {onboardingChrome ? renderOnboardingChrome() : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pb: 2 }}>
            <Typography color={"text.primary"}>
              {t("schemes.otpSentMessage", { mobile: mobileNumber })}
            </Typography>
            {onEditNumber && (
              <IconButton
                size="small"
                aria-label={t("schemes.editMobileNumber")}
                onClick={onEditNumber}
                sx={{ color: theme.palette.primary.main }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Typography
            sx={{ fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "text.primary", pb: "4px" }}
          >
            {t("schemes.enterOTP")}
          </Typography>
        </>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleOTPClick();
        }}
        style={{ width: "100%" }}
      >
        <Box sx={{ mt: onboardingChrome ? ONBOARDING_OTP_GAP_INPUTS : 0, width: "100%" }}>
        {layout === "inline" ? (
          <Box
            sx={{
              display: "flex",
              columnGap: "2rem",
              rowGap: "1rem",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "start",
            }}
          >
            {renderOtpInputBlock()}
            {!isSubmit && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Button
                  type="submit"
                  variant="primary" size="submit"
                  disabled={otpValue.length < 6 || verificationLoading}
                  sx={{ width: "auto", minWidth: "120px" }}
                >
                  {t("schemes.verify")}
                </Button>
                {verificationLoading && (
                  <AccessibleCircularProgress color="success" size="32px" thickness={6} />
                )}
              </Box>
            )}
          </Box>
        ) : (
          <>
            {renderOtpInputBlock()}
            {!isSubmit && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: onboardingChrome ? 0 : 2,
                }}
              >
                <Button
                  type="submit"
                  variant="primary" size="submit"
                  fullWidth={onboardingChrome}
                  disabled={otpValue.length < 6 || verificationLoading}
                >
                  {t("schemes.verify")}
                </Button>
                {verificationLoading && (
                  <AccessibleCircularProgress color="success" size="32px" thickness={6} />
                )}
              </Box>
            )}
          </>
        )}
        </Box>
      </form>
    </Box>
  );
};

// Exported entry point — always renders OtpInputRendererBody inside the
// rewamp theme (Bihar Krishi green pill/card chrome), regardless of the
// ambient theme the caller lives under. This keeps every consumer visually
// consistent: FarmerLoginForm (RewampLoginPage), FarmerDbtVerification, and InlineFarmerOtp
// already render inside RewampLayout's rewamp ThemeProvider, but the 2 GRM
// call sites (TrackService.js, GrmCreateForm.js) render under the legacy
// theme — this wrapper re-themes just this one body, the same "re-theme one
// body" move RewampRoutes.js's HomeLanding/hortiWrap use for legacy-themed
// pages.
export const OtpInputRenderer = (props) => {
  const { fontSize, ourTheme } = useContext(AppLayoutContext);
  const rewampTheme = useMemo(
    () => generateRewampTheme(fontSize, ourTheme),
    [fontSize, ourTheme],
  );
  return (
    <ThemeProvider theme={rewampTheme}>
      <OtpInputRendererBody {...props} />
    </ThemeProvider>
  );
};

function FarmerDbtVerification({
  handleVerifyOtp,
  otpValidationError,
  setOtpValidationError,
  otpValue,
  setOtpValue,
  categoryType = "PROFILE_GET",
  userType = "CITIZEN",
  verificationLoading = false,
  headerText = "",
  mdmsId,
  onOtpStepChange,
}) {
  const identifierModes = identifierModesFor(categoryType);
  const showModeTabs = hasIdentifierTabs(categoryType);
  const [idMode, setIdMode] = useState(identifierModes[0]); // "dbtId" | "mobile"
  const [dbtValue, setDbtValue] = useState("");
  const [mobileValue, setMobileValue] = useState("");
  // Server-echoed number from _send (masked for DBT lookup); mobile-verify
  // stores the agent-typed full number instead.
  const [mobileNumber, setMobileNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [waitTime, setWaitTime] = useState(null);
  // Inline error banner (Figma 639-82351, "Incorrect DBT ID") — persists next
  // to the field; the single error surface for this form (no toast alongside it).
  const [identifierError, setIdentifierError] = useState("");
  const { t } = useTranslation();
  useRewampLocalization(["farmerVerify"]);
  const tRewamp = useRewampT();
  const [headerKey, headerDefault] = resolveFarmerVerifyHeader(headerText);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { open: urlDialogOpen, externalUrl, handleLinkClick, handleClose: handleUrlDialogClose } =
    useUrlDialog();

  const isMobileMode = idMode === "mobile";
  const identifierValue = isMobileMode ? mobileValue : dbtValue;
  const requiredLength = isMobileMode ? 10 : 13;
  const otpSent = Boolean(mobileNumber);

  // Same DBT/Non-DBT hint used on the login page's mobile field (mobile-parity
  // pre-OTP lookup) — only meaningful in mobile-verify mode, DBT-ID mode
  // already names the identifier directly.
  const {
    loading: dbtHintLoading,
    isRegistered: dbtIsRegistered,
  } = useDbtRegistrationHint(isMobileMode ? identifierValue : "");
  const dbtBadgeLabel =
    !isMobileMode || dbtIsRegistered === null
      ? null
      : dbtIsRegistered
        ? t("ONBOARDING_SIGNIN_DBT")
        : t("ONBOARDING_SIGNIN_NON_DBT");

  const location = useLocation();
  const handleRecover = () => {
    navigate(`${window.contextPath || ""}/recover-dbt-id`, { state: { from: location } });
  };

  const handleModeChange = (event) => {
    // Switching identifier clears both inputs and any sent-OTP state so the
    // "OTP sent" copy / resend timer never point at a stale identifier.
    setIdMode(event.target.value);
    setDbtValue("");
    setMobileValue("");
    setMobileNumber("");
    setWaitTime(null);
    setOtpValue("");
    setOtpValidationError({ message: "" });
    setIdentifierError("");
  };

  const handleDbtChange = (event) => {
    const { value } = event.target;
    if (/^\d*$/.test(value) && value.length <= 13) {
      setDbtValue(value);
      setIdentifierError("");
    }
  };

  const handleMobileChange = (event) => {
    const { value } = event.target;
    if (/^\d*$/.test(value) && value.length <= 10) {
      setMobileValue(value);
      setIdentifierError("");
    }
  };

  const handleClick = async () => {
    setIdentifierError("");
    if (identifierValue.length === 0) {
      const code = isMobileMode ? "EMPTY_MOBILE_NUMBER" : "EMPTY_DBT_ID";
      setIdentifierError(code);
      return;
    } else if (identifierValue.length < requiredLength) {
      const code = isMobileMode ? "INVALID_MOBILE_NUMBER" : "INVALID_DBT_ID";
      setIdentifierError(code);
      return;
    }
    const requestData = {
      otp: {
        mobileNumber: isMobileMode ? mobileValue : null,
        dbtId: isMobileMode ? null : dbtValue,
        categoryType,
        userType,
        tenantId: "br",
        // SCHEME_APPLY_PROFILE_GET scopes the identity OTP to one scheme.
        ...(mdmsId ? { mdmsId } : {}),
      }, 
    };
    setLoading(true);
    try {
      const response = await SchemeService.otpFarmerSchemeGet(requestData);

      if (response?.ResponseInfo?.status === "successful") {
        const contact = response?.message;
        setMobileNumber(isMobileMode ? mobileValue : contact);
        dispatchNotification("success", [t("schemes.otpSuccess")], dispatch);
        setWaitTime(OTP_RESEND_SECONDS);
        setOtpValue("");
      } else {
        const code = response?.Errors?.[0]?.code || "SOMETHING_WENT_WRONG";
        if (code === OTP_RATE_LIMIT_CODE) {
          setIdentifierError("COMMON_MOBILE_RATE_LIMIT");
          return;
        }
        throw new Error(code);
      }
    } catch (error) {
      setIdentifierError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Figma 639-88408's edit-pencil — return to the identifier step without
  // losing what was typed (dbtValue/mobileValue survive; only the
  // "OTP sent" state resets). Contrast with handleModeChange, which is a
  // full reset including the identifier values.
  const handleEditNumber = () => {
    setMobileNumber("");
    setWaitTime(null);
    setOtpValue("");
    setOtpValidationError({ message: "" });
    setIdentifierError("");
  };

  // Breadcrumb's Back pill lives outside this component (FarmerContextRoute).
  // Report the "go back to identifier step" handler only while the OTP
  // screen is showing, so Back can return here instead of leaving the page;
  // on the identifier step itself, null restores the breadcrumb's default
  // (up-one-level) behaviour.
  useEffect(() => {
    onOtpStepChange?.(otpSent ? handleEditNumber : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpSent]);

  return (
    // Same Paper the onboarding pages use (login / reset / recover) holds the header and the
    // identifier / OTP step; the helper lines sit below it in the same column.
    <Box sx={{ width: "100%", maxWidth: LOGIN_CARD_MAX_WIDTH, mx: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper variant="onboardingCard">
        {!otpSent ? (
          <Typography
            variant="h2"
            component="h2"
            // Same inline ramp as RewampLoginPage's card heading (Figma Heading1, 20px) until phase 2 lands a variant.
            sx={{ fontWeight: 700, fontSize: "1.25rem", lineHeight: "30px", textAlign: "center", width: "100%", color: "neutral.headings" }}
          >
            {tRewamp(headerKey, headerDefault)}
          </Typography>
        ) : null}

        {!otpSent ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
            {showModeTabs && (
              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <Typography component="legend" sx={visuallyHiddenSx}>
                  {t("schemes.verifyUsing")}
                </Typography>
                {/* Two equal halves, each radio + label left-aligned (Figma 639-82351); radio→label gap 8px. */}
                <RadioGroup
                  row
                  name="farmer-identifier-mode"
                  value={idMode}
                  onChange={handleModeChange}
                  sx={{
                    width: "100%",
                    flexWrap: "nowrap",
                    borderBottom: 2,
                    borderColor: "divider",
                  }}
                >
                  {identifierModes.map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio color="primary" sx={{ p: 0 }} />}
                      label={t(IDENTIFIER_MODE_LABEL_KEYS[value])}
                      sx={{ flex: "1 1 0", m: 0, pb: 1.5, gap: 1, justifyContent: "flex-start", minWidth: 0 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleClick();
              }}
              noValidate
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              <RewampFormField
                label={
                  isMobileMode
                    ? t("schemes.farmerMobileNumber")
                    : t("schemes.farmerDBTId")
                }
                required
                value={identifierValue}
                onChange={isMobileMode ? handleMobileChange : handleDbtChange}
                placeholder={
                  isMobileMode
                    ? t("schemes.farmerMobileNumberPlaceholder")
                    : t("schemes.farmerDBTIdPlaceholder")
                }
                inputProps={{ inputMode: "numeric", maxLength: requiredLength }}
                InputProps={
                  isMobileMode
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <DbtRegistrationBadge loading={dbtHintLoading} label={dbtBadgeLabel} />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
              {identifierError && (
                <RewampFeedbackBanner
                  variant="error"
                  message={
                    REWAMP_IDENTIFIER_ERRORS[identifierError]
                      ? tRewamp(...REWAMP_IDENTIFIER_ERRORS[identifierError])
                      : t(identifierError)
                  }
                />
              )}
              <Button
                type="submit"
                variant="primary"
                size="submit"
                disabled={loading || identifierValue.length < requiredLength}
              >
                {t("schemes.getOTP")}
              </Button>
            </Box>
            {loading && (
              <Typography color="text.textGreen">
                {t("schemes.sendingOTP")}...
              </Typography>
            )}
          </Box>
        ) : (
          <OtpInputRenderer
            bare
            onboardingChrome
            otpSubheaderKey={isMobileMode ? "ONBOARDING_RECOVER_OTP_TITLE" : "ONBOARDING_DBT_ID_OTP_TITLE"}
            mobileNumber={mobileNumber}
            mobileNumberDisplay={isMobileMode ? mobileValue : mobileNumber}
            otpValue={otpValue}
            setOtpValue={setOtpValue}
            otpValidationError={otpValidationError}
            setOtpValidationError={setOtpValidationError}
            handleVerifyOtp={() =>
              handleVerifyOtp(
                otpValue,
                isMobileMode
                  ? { mobileNumber: mobileValue }
                  : { dbtId: dbtValue },
              )
            }
            dispatch={dispatch}
            t={t}
            waitTime={waitTime}
            setWaitTime={setWaitTime}
            onResendOtp={handleClick}
            onEditNumber={handleEditNumber}
            verificationLoading={verificationLoading}
          />
        )}
      </Paper>
      {/* Figma 639-82351: helper lines sit below the card, flush with its left edge, not inside it. */}
      {!otpSent && !isMobileMode && (
        <RewampDbtHelperLinks
          onRecover={handleRecover}
          onRegister={() => handleLinkClick(DBT_REGISTRATION_PORTAL_URL)}
          rowGap={0.5}
        />
      )}
      <UrlDialog
        open={urlDialogOpen}
        externalUrl={externalUrl}
        handleClose={handleUrlDialogClose}
        t={t}
      />
    </Box>
  );
}

export default FarmerDbtVerification;
